import * as React from "react";
import {
  Sheet,
  SheetContent,
  SheetGradientHeader,
  sheetGradientCloseButtonClass,
  Button,
  Input,
  Textarea,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@repo/ui";
import {
  Bell,
  CalendarDays,
  Loader2,
  Link2,
  Pill,
  Repeat,
  SlidersHorizontal,
  UserCircle,
  X,
  User,
  Building2,
} from "lucide-react";
import { formatToApiDate, cn } from "@repo/utils";
import {
  InventorySearchCombobox,
  type InventoryItem,
} from "@/components/forms/inventory-search-combobox";
import {
  DateInput,
  PatientCombobox,
  PatientManagementModal,
} from "@/components/forms";
import type { Patient } from "@/lib/types/patients";
import { ApiClientError } from "@/lib/api/client";
import { getInventoryItem } from "@/lib/api/inventory";
import { useApproval } from "@/context/approval-context";
import { useAuth } from "@/lib/auth/auth-provider";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import type {
  Reminder,
  CreateReminderInput,
  UpdateReminderInput,
  ReminderVisibility,
  ReminderPriority,
  ReminderReferenceType,
  ReminderRecurrenceType,
} from "@/hooks/use-reminders";

interface CreateReminderDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateReminderInput) => Promise<Reminder | null>;
  onUpdate?: (
    id: string,
    input: UpdateReminderInput
  ) => Promise<Reminder | null>;
  editingReminder?: Reminder | null;
  // Pre-fill from context menu (entity references)
  prefillReferenceType?: ReminderReferenceType;
  prefillReferenceId?: string;
  prefillReferenceName?: string;
  // General pre-fill (e.g. from alerts)
  prefillTitle?: string;
  prefillDescription?: string;
  prefillPriority?: ReminderPriority;
  prefillVisibility?: ReminderVisibility;
  // Pre-fill patient (e.g. from patients tab)
  prefillPatientId?: string;
  prefillPatientName?: string;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

const DATE_SHORTCUTS = [
  { label: "Today", getDate: () => new Date() },
  { label: "Tomorrow", getDate: () => addDays(new Date(), 1) },
  { label: "In 3 Days", getDate: () => addDays(new Date(), 3) },
  { label: "In a Week", getDate: () => addDays(new Date(), 7) },
  { label: "In a Month", getDate: () => addDays(new Date(), 30) },
];

export function CreateReminderDialog({
  open,
  onClose,
  onSubmit,
  onUpdate,
  editingReminder,
  prefillReferenceType,
  prefillReferenceId,
  prefillReferenceName,
  prefillTitle,
  prefillDescription,
  prefillPriority,
  prefillVisibility,
  prefillPatientId,
  prefillPatientName,
}: CreateReminderDialogProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const auth = useAuth();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [visibility, setVisibility] =
    React.useState<ReminderVisibility>("pharmacy");
  const [priority, setPriority] = React.useState<ReminderPriority>("standard");
  const [dueDate, setDueDate] = React.useState("");
  const [dueTime, setDueTime] = React.useState("");
  const [recurrenceType, setRecurrenceType] =
    React.useState<ReminderRecurrenceType>("none");
  const [recurrenceInterval, setRecurrenceInterval] = React.useState(1);
  const [recurrenceEndDate, setRecurrenceEndDate] = React.useState("");
  const [linkedDrug, setLinkedDrug] = React.useState<InventoryItem | null>(
    null
  );
  const [linkedPatient, setLinkedPatient] = React.useState<Patient | null>(
    null
  );
  const [showCreatePatient, setShowCreatePatient] = React.useState(false);
  const [createPatientName, setCreatePatientName] = React.useState<
    string | undefined
  >(undefined);
  const [patientsRefreshTrigger, setPatientsRefreshTrigger] = React.useState(0);
  // Preserve non-inventory/patient references (receipt, dispense, adjustment) that the pickers don't manage
  const [preservedRef, setPreservedRef] = React.useState<{
    type: ReminderReferenceType;
    id: string;
  } | null>(null);

  const isEditing = !!editingReminder;
  const isRecurring = recurrenceType !== "none";
  // Only the creator may flip a shared reminder to personal (API enforces this).
  const canChangeVisibility =
    !isEditing || auth.user?.id === editingReminder?.created_by;

  // Initialize form when dialog opens
  React.useEffect(() => {
    if (open) {
      if (editingReminder) {
        setTitle(editingReminder.title);
        setDescription(editingReminder.description || "");
        setVisibility(editingReminder.visibility);
        setPriority(editingReminder.priority);
        setDueDate(editingReminder.due_date);
        setDueTime(editingReminder.due_time?.slice(0, 5) || "");
        setRecurrenceType(editingReminder.recurrence_type || "none");
        setRecurrenceInterval(editingReminder.recurrence_interval || 1);
        setRecurrenceEndDate(editingReminder.recurrence_end_date || "");
        // Entity reference (drug or other item)
        if (
          editingReminder.reference_type === "inventory" &&
          editingReminder.reference_id
        ) {
          setLinkedDrug({
            id: editingReminder.reference_id,
            itemName: "",
            ndc: "",
            drugType: "",
            label: "",
          });
          setPreservedRef(null);
        } else if (
          editingReminder.reference_type &&
          editingReminder.reference_id
        ) {
          // Non-inventory reference — preserve it since the pickers can't manage it
          setLinkedDrug(null);
          setPreservedRef({
            type: editingReminder.reference_type,
            id: editingReminder.reference_id,
          });
        } else {
          setLinkedDrug(null);
          setPreservedRef(null);
        }

        // Patient link (independent from entity reference)
        if (editingReminder.patient_id) {
          setLinkedPatient({
            id: editingReminder.patient_id,
            pharmacy_id: "",
            first_name: editingReminder.patient_name || "",
            last_name: null,
            middle_name: null,
            date_of_birth: null,
            phone_number: null,
            street: null,
            street_2: null,
            city: null,
            state: null,
            zip: null,
            dispense_count: 0,
            reminder_count: 0,
            created_at: "",
            updated_at: "",
          });
        } else {
          setLinkedPatient(null);
        }
      } else {
        setTitle(
          prefillTitle ||
            (prefillReferenceName ? `Reminder: ${prefillReferenceName}` : "")
        );
        setDescription(prefillDescription || "");
        setVisibility(prefillVisibility || "pharmacy");
        setPriority(prefillPriority || "standard");
        setDueDate(formatToApiDate(addDays(new Date(), 1)));
        setDueTime("");
        setRecurrenceType("none");
        setRecurrenceInterval(1);
        setRecurrenceEndDate("");
        setPreservedRef(null);
        // Pre-populate linked drug from prefill
        if (prefillReferenceType === "inventory" && prefillReferenceId) {
          setLinkedDrug({
            id: prefillReferenceId,
            itemName: prefillReferenceName || "",
            ndc: "",
            drugType: "",
            label: prefillReferenceName || "",
          });
        } else {
          setLinkedDrug(null);
        }
        // Pre-populate linked patient from prefill
        if (prefillPatientId && prefillPatientName) {
          setLinkedPatient({
            id: prefillPatientId,
            first_name: prefillPatientName,
            last_name: null,
            middle_name: null,
            date_of_birth: null,
            phone_number: null,
            street: null,
            street_2: null,
            city: null,
            state: null,
            zip: null,
            pharmacy_id: "",
            dispense_count: 0,
            reminder_count: 0,
            created_at: "",
            updated_at: "",
          } as Patient);
        } else {
          setLinkedPatient(null);
        }
      }
    }
  }, [
    open,
    editingReminder,
    prefillReferenceType,
    prefillReferenceId,
    prefillReferenceName,
    prefillPatientId,
    prefillPatientName,
    prefillTitle,
    prefillDescription,
    prefillPriority,
    prefillVisibility,
  ]);

  // Fetch inventory item details when editing a reminder with a linked drug
  React.useEffect(() => {
    if (
      !open ||
      !editingReminder?.reference_id ||
      editingReminder.reference_type !== "inventory"
    )
      return;

    let cancelled = false;
    const expectedId = editingReminder.reference_id;

    getInventoryItem(expectedId)
      .then((res) => {
        if (cancelled) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const item = (res as any).data || res;
        // Only update if the user hasn't already changed or cleared the linked drug
        setLinkedDrug((prev) => {
          if (!prev || prev.id !== expectedId) return prev;
          return {
            id: expectedId,
            itemName: item.item_name || item.name || "",
            ndc: item.ndc || "",
            drugType: item.drug_type || "",
            label: item.item_name || item.name || "",
          };
        });
      })
      .catch(() => {
        // Keep the placeholder state — user can still change or remove
      });

    return () => {
      cancelled = true;
    };
  }, [open, editingReminder?.reference_id, editingReminder?.reference_type]);

  // Fetch patient details when editing a reminder with a linked patient
  React.useEffect(() => {
    if (!open || !editingReminder?.patient_id) return;

    let cancelled = false;
    const expectedId = editingReminder.patient_id;
    const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3005";

    fetch(`${API_URL}/patients/${expectedId}`, { credentials: "include" })
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        const patient = json.patient || json.data || json;
        setLinkedPatient((prev) => {
          if (!prev || prev.id !== expectedId) return prev;
          return { ...prev, ...patient };
        });
      })
      .catch(() => {
        // Keep the placeholder state — user can still change or remove
      });

    return () => {
      cancelled = true;
    };
  }, [open, editingReminder?.patient_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !dueDate) return;

    setIsSubmitting(true);

    try {
      if (isEditing && onUpdate && editingReminder) {
        const result = await onUpdate(editingReminder.id, {
          title: title.trim(),
          description: description.trim() || null,
          visibility,
          priority,
          due_date: dueDate,
          due_time: dueTime || null,
          reference_type: linkedDrug
            ? "inventory"
            : (preservedRef?.type ?? null),
          reference_id: linkedDrug?.id ?? preservedRef?.id ?? null,
          patient_id: linkedPatient?.id ?? null,
          recurrence_type: recurrenceType,
          recurrence_interval: isRecurring ? recurrenceInterval : 1,
          recurrence_end_date:
            isRecurring && recurrenceEndDate ? recurrenceEndDate : null,
        });

        if (result) {
          toast({
            title: "Reminder updated",
            description: "Your reminder has been updated successfully.",
            variant: "success",
          });
          onClose();
        } else {
          toast({
            title: "Error",
            description: "Failed to update reminder.",
            variant: "destructive",
          });
        }
      } else {
        const input: CreateReminderInput = {
          title: title.trim(),
          description: description.trim() || undefined,
          visibility,
          priority,
          due_date: dueDate,
          due_time: dueTime || undefined,
          reference_type: linkedDrug ? "inventory" : undefined,
          reference_id: linkedDrug?.id,
          patient_id: linkedPatient?.id,
          recurrence_type: recurrenceType,
          recurrence_interval: isRecurring ? recurrenceInterval : undefined,
          recurrence_end_date:
            isRecurring && recurrenceEndDate ? recurrenceEndDate : undefined,
        };

        const result = await onSubmit(input);

        if (result) {
          toast({
            title: "Reminder created",
            description: "Your reminder has been created successfully.",
            variant: "success",
          });
          onClose();
        } else {
          toast({
            title: "Error",
            description: "Failed to create reminder.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async () => {},
          actionDescription: isEditing ? "Update reminder" : "Create reminder",
        })
      )
        return;
      console.error("Reminder submission failed:", error);
      toast({
        title: "An unexpected error occurred",
        description:
          error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent
          side="right"
          className={cn(
            "flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl",
            sheetGradientCloseButtonClass
          )}
        >
          <SheetGradientHeader
            icon={Bell}
            title={isEditing ? "Edit Reminder" : "Create Reminder"}
            description={
              isEditing
                ? "Update the details for this reminder."
                : "Set a reminder for yourself or your pharmacy team."
            }
          />

          <form
            onSubmit={handleSubmit}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid gap-6 md:grid-cols-[1fr_320px]">
                {/* LEFT COLUMN — What & When */}
                <div className="space-y-5">
                  {/* Title */}
                  <div className="space-y-2">
                    <Label htmlFor="reminder-title">Title *</Label>
                    <Input
                      id="reminder-title"
                      data-testid="reminder-form-title"
                      placeholder="e.g., Reorder Adderall XR, Follow up on RX #12345"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      maxLength={255}
                      required
                      autoFocus
                    />
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="reminder-description">Description</Label>
                    <Textarea
                      id="reminder-description"
                      data-testid="reminder-form-description"
                      placeholder="Add more details about this reminder..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={2000}
                      rows={3}
                    />
                  </div>

                  {/* When section */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {isRecurring ? "Starting From *" : "When *"}
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {DATE_SHORTCUTS.map((shortcut) => {
                        const shortcutDate = formatToApiDate(
                          shortcut.getDate()
                        );
                        const isActive = dueDate === shortcutDate;
                        return (
                          <Button
                            key={shortcut.label}
                            type="button"
                            size="sm"
                            variant={isActive ? "default" : "outline"}
                            className={cn(
                              "h-7 text-xs transition-transform",
                              isActive && "scale-105"
                            )}
                            onClick={() => setDueDate(shortcutDate)}
                          >
                            {shortcut.label}
                          </Button>
                        );
                      })}
                    </div>
                    <DateInput
                      id="reminder-date"
                      data-testid="reminder-form-date"
                      value={dueDate}
                      onChange={(v) => setDueDate(v)}
                    />
                  </div>

                  {/* Repeat section */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-1.5">
                      <Repeat className="h-3.5 w-3.5" />
                      Repeat
                    </Label>
                    <Select
                      value={recurrenceType}
                      onValueChange={(v) =>
                        setRecurrenceType(v as ReminderRecurrenceType)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Does not repeat</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>

                    {isRecurring && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="recurrence-interval">Every</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="recurrence-interval"
                              type="number"
                              min={1}
                              max={365}
                              value={recurrenceInterval}
                              onChange={(e) =>
                                setRecurrenceInterval(
                                  Math.max(1, parseInt(e.target.value) || 1)
                                )
                              }
                              className="w-20"
                            />
                            <span className="text-sm text-muted-foreground">
                              {recurrenceType === "daily"
                                ? recurrenceInterval === 1
                                  ? "day"
                                  : "days"
                                : recurrenceType === "weekly"
                                  ? recurrenceInterval === 1
                                    ? "week"
                                    : "weeks"
                                  : recurrenceType === "monthly"
                                    ? recurrenceInterval === 1
                                      ? "month"
                                      : "months"
                                    : recurrenceInterval === 1
                                      ? "year"
                                      : "years"}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="recurrence-end">
                            Ends on (optional)
                          </Label>
                          <DateInput
                            id="recurrence-end"
                            value={recurrenceEndDate}
                            onChange={(v) => setRecurrenceEndDate(v)}
                            min={dueDate}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN — Link & Options cards */}
                <div className="space-y-4">
                  {/* Link card */}
                  <div className="space-y-4 rounded-xl border border-primary-soft bg-primary-soft/30 p-4">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                        <Link2 className="h-3.5 w-3.5 text-white" />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground">
                        Link to
                      </h3>
                    </div>

                    {/* Drug */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5 text-xs">
                        <Pill className="h-3.5 w-3.5" />
                        Drug
                      </Label>
                      {linkedDrug ? (
                        <div className="flex items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <InventorySearchCombobox
                              compact
                              valueItem={
                                linkedDrug.itemName ? linkedDrug : undefined
                              }
                              onSelect={(item) => {
                                setLinkedDrug(item);
                              }}
                              displayLabel={
                                linkedDrug.itemName ||
                                "Linked drug — click to change"
                              }
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                            onClick={() => setLinkedDrug(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <InventorySearchCombobox
                          compact
                          onSelect={(item) => {
                            setLinkedDrug(item);
                          }}
                          placeholder="Link a drug..."
                        />
                      )}
                    </div>

                    {/* Patient */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5 text-xs">
                        <UserCircle className="h-3.5 w-3.5" />
                        Patient
                      </Label>
                      {linkedPatient ? (
                        <div className="flex items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <PatientCombobox
                              value={linkedPatient.id}
                              onSelect={(patient) => {
                                setLinkedPatient(patient);
                              }}
                              displayLabel={
                                linkedPatient.first_name
                                  ? `${linkedPatient.first_name}${linkedPatient.last_name ? " " + linkedPatient.last_name : ""}`
                                  : "Linked patient — click to change"
                              }
                              onCreateClick={(searchText) => {
                                setCreatePatientName(searchText);
                                setShowCreatePatient(true);
                              }}
                              refreshTrigger={patientsRefreshTrigger}
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                            onClick={() => setLinkedPatient(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <PatientCombobox
                          onSelect={(patient) => {
                            setLinkedPatient(patient);
                          }}
                          placeholder="Link a patient..."
                          onCreateClick={(searchText) => {
                            setCreatePatientName(searchText);
                            setShowCreatePatient(true);
                          }}
                          refreshTrigger={patientsRefreshTrigger}
                        />
                      )}
                    </div>
                  </div>

                  {/* Options card */}
                  <div className="space-y-4 rounded-xl border border-secondary-soft bg-secondary-soft/30 p-4">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary">
                        <SlidersHorizontal className="h-3.5 w-3.5 text-white" />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground">
                        Options
                      </h3>
                    </div>

                    {/* Visibility */}
                    <div className="space-y-2">
                      <Label className="text-xs">Visibility</Label>
                      <div className="flex items-center gap-1 rounded-lg border bg-muted/50 p-1">
                        <Button
                          type="button"
                          variant={
                            visibility === "personal" ? "default" : "ghost"
                          }
                          size="sm"
                          className="flex-1 gap-2"
                          disabled={!canChangeVisibility}
                          onClick={() => setVisibility("personal")}
                        >
                          <User className="h-4 w-4" />
                          Personal
                        </Button>
                        <Button
                          type="button"
                          variant={
                            visibility === "pharmacy" ? "default" : "ghost"
                          }
                          size="sm"
                          className="flex-1 gap-2"
                          disabled={!canChangeVisibility}
                          onClick={() => setVisibility("pharmacy")}
                        >
                          <Building2 className="h-4 w-4" />
                          Pharmacy
                        </Button>
                      </div>
                    </div>

                    {/* Priority */}
                    <div className="space-y-2">
                      <Label className="text-xs">Priority</Label>
                      <div className="flex items-center gap-1 rounded-lg border bg-muted/50 p-1">
                        <Button
                          type="button"
                          variant={
                            priority === "standard" ? "default" : "ghost"
                          }
                          size="sm"
                          className="flex-1 gap-2"
                          onClick={() => setPriority("standard")}
                        >
                          <span className="h-2 w-2 rounded-full bg-warning" />
                          Standard
                        </Button>
                        <Button
                          type="button"
                          variant={priority === "urgent" ? "default" : "ghost"}
                          size="sm"
                          className="flex-1 gap-2"
                          onClick={() => setPriority("urgent")}
                        >
                          <span
                            className={cn(
                              "h-2 w-2 rounded-full bg-destructive",
                              priority === "urgent" &&
                                "animate-pulse ring-2 ring-destructive/40"
                            )}
                          />
                          Urgent
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky footer */}
            <div className="shrink-0 border-t bg-background/95 px-6 py-4 backdrop-blur">
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="reminder-form-submit"
                  disabled={isSubmitting || !title.trim() || !dueDate}
                  className="group gap-2"
                >
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Bell className="h-4 w-4 transition-transform group-hover:rotate-12" />
                  )}
                  {isEditing ? "Save Changes" : "Create Reminder"}
                </Button>
              </div>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <PatientManagementModal
        open={showCreatePatient}
        onOpenChange={(o) => {
          setShowCreatePatient(o);
          if (!o) setCreatePatientName(undefined);
        }}
        defaultName={createPatientName}
        onPatientsChange={() => setPatientsRefreshTrigger((prev) => prev + 1)}
        onCreated={(newPatient) => {
          setLinkedPatient(newPatient);
          setCreatePatientName(undefined);
        }}
      />
    </>
  );
}
